import { useEffect, useRef, useState } from 'react';

/**
 * Tracks whether an element has entered the viewport (sticky: stays `true`
 * once seen). All callers share a single module-level `IntersectionObserver`
 * per `rootMargin`.
 *
 * Why shared: creating one observer per element breaks down when many
 * components mount in the same commit (e.g. expanding a folder with hundreds
 * of leaves) — browsers throttle/drop initial-intersection callbacks for the
 * burst of new observers, so most elements never get marked visible. A single
 * long-lived observer with many subscribers avoids that cliff and is cheaper.
 */

type Subscriber = (visible: boolean) => void;

type SharedObserver = {
  io: IntersectionObserver;
  subscribers: Map<Element, Subscriber>;
};

const observers = new Map<string, SharedObserver>();

const getSharedObserver = (rootMargin: string): SharedObserver => {
  const existing = observers.get(rootMargin);
  if (existing) {
    return existing;
  }

  const subscribers = new Map<Element, Subscriber>();
  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) {
          continue;
        }
        const cb = subscribers.get(entry.target);
        if (cb) {
          cb(true);
        }
      }
    },
    { rootMargin, threshold: 0 },
  );

  const shared: SharedObserver = { io, subscribers };
  observers.set(rootMargin, shared);
  return shared;
};

const useSharedVisibility = <T extends HTMLElement = HTMLElement>(
  rootMargin: string = '100px',
) => {
  const ref = useRef<T | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return undefined;
    }

    const shared = getSharedObserver(rootMargin);
    shared.subscribers.set(el, (next) => setVisible(next));
    shared.io.observe(el);

    return () => {
      shared.subscribers.delete(el);
      shared.io.unobserve(el);
    };
  }, [rootMargin]);

  return [ref, visible] as const;
};

export default useSharedVisibility;
